import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Routes } from '@angular/router';
import { CanDeactivateGuard } from '@directives/can-deactivate/can-deactivate.guard';
import { AuthGuardService } from '@guards/auth-guard/auth-guard.service';
import { SuperAdminGuardService } from '@guards/super-admin-guard/super-admin-guard.service';

const routes: Routes = [

  { path: 'login', loadComponent: () => import('@views/login/login/login.component').then(m => m.LoginComponent) },
  { path: 'registrarse', loadComponent: () => import('@views/login/form-usuario-registro/form-usuario-registro.component').then(m => m.FormUsuarioRegistroComponent) },
  { path: 'verificarCorreo', loadComponent: () => import('@views/login/form-verificar-cuenta-usuario/form-verificar-cuenta-usuario.component').then(m => m.FormVerificarCuentaUsuarioComponent) },
   { path: 'confirmar-registro', loadComponent: () => import('@views/login/confirmar-registro/confirmar-registro.component').then(m => m.ConfirmarRegistroComponent) },
  { path: 'bienvenido', loadComponent: () => import('@views/login/bienvenida/bienvenida.component').then(m => m.BienvenidaComponent) },
  { path: 'restablecerContrasena', loadComponent: () => import('@views/login/recoverPassword/recoverPassword.component').then(m => m.RecoverPasswordComponent) },
  { path: 'primerFactor', loadComponent: () => import('@views/login/primer-factor/primer-factor.component').then(m => m.PrimerFactorComponent) },
  { path: 'cambiarContrasena', loadComponent: () => import('@views/login/cambiar-contrasena/cambiar-contrasena.component').then(m => m.CambiarContrasenaComponent) },
  { path: 'nuevaContrasena', loadComponent: () => import('@views/login/nueva-contrasena/nueva-contrasena.component').then(m => m.NuevaContrasenaComponent) },
  {
    path: 'seleccionar-empresa',
    loadComponent: () => import('@views/empresa/seleccionar-empresa/seleccionar-empresa.component').then(m => m.SeleccionarEmpresaComponent)
  },
  // Páginas públicas (sin AuthGuard) a las que MercadoPago redirige al pagador después del
  // checkout. No dependen de que la sesión de DICEA siga activa.
  { path: 'pago-exitoso', loadComponent: () => import('@views/facturacion/pago-resultado/pago-resultado.component').then(m => m.PagoResultadoComponent), data: { estado: 'exitoso' } },
  { path: 'pago-fallido', loadComponent: () => import('@views/facturacion/pago-resultado/pago-resultado.component').then(m => m.PagoResultadoComponent), data: { estado: 'fallido' } },
  { path: 'pago-pendiente', loadComponent: () => import('@views/facturacion/pago-resultado/pago-resultado.component').then(m => m.PagoResultadoComponent), data: { estado: 'pendiente' } },
  // Pantalla pública a la que llega el usuario invitado desde el correo de invitación al
  // vincularlo a una nueva empresa (ver EmpresaUsuarioService.VincularUsuarioExistente).
  { path: 'confirmar-vinculacion', loadComponent: () => import('@views/empresa-usuario/confirmar-vinculacion/confirmar-vinculacion.component').then(m => m.ConfirmarVinculacionComponent) },
  // Auditoría QA (2026-08-04) — Hallazgo crítico #2 (módulo Ley 25.326 - Cumplimiento Legal
  // Formal): rutas públicas (sin AuthGuard) de Política de Privacidad y Términos de Servicio —
  // tienen que poder leerse antes/sin necesidad de loguearse. Mismo componente, sólo cambia el
  // "tipo" que determina qué documento vigente pide (ver LegalController).
  { path: 'politica-privacidad', loadComponent: () => import('@views/legal/legal-documento/legal-documento.component').then(m => m.LegalDocumentoComponent), data: { tipo: 'PoliticaPrivacidad' } },
  { path: 'terminos-servicio', loadComponent: () => import('@views/legal/legal-documento/legal-documento.component').then(m => m.LegalDocumentoComponent), data: { tipo: 'TerminosServicioUsuario' } },
  { path: 'terminos-servicio-empresa', loadComponent: () => import('@views/legal/legal-documento/legal-documento.component').then(m => m.LegalDocumentoComponent), data: { tipo: 'TerminosServicioEmpresa' } },
  // Auditoría UI (2026-07-30): páginas de error propias, con la paleta de marca.
  { path: 'error/500', loadComponent: () => import('@views/errors/error500/error500.component').then(m => m.Error500Component) },
  {
    path: '', loadComponent: () => import('@views/inicio/inicio.component').then(m => m.InicioComponent),
    canActivate: [AuthGuardService],
    data: {
      title: 'Inicio',
      breadcrumb: 'Inicio'
    },
    children: [
      {
        path: 'cargando',
        loadComponent: () => import('@views/login/cargando-inicio-app/cargando-inicio-app.component').then(m => m.CargandoInicioAppComponent),
        canActivate: [AuthGuardService],
        data: {
        },
      },
      {
        path: 'dashboard',
        loadComponent: () => import('@views/dashboard/dashboard/dashboard.component').then(m => m.DashboardComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Dashboard',
          breadcrumb: {
            label: 'Dashboard',
          }


        },
      },
      {
        path: 'perfilUsuarios',
        loadComponent: () => import('@views/perfil-usuario/perfil-usuario.component').then(m => m.PerfilUsuarioComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Perfil de usuario',
          breadcrumb: 'Perfil Usuario'
        },
      },
      {
        path: 'validador-firma-digital',
        loadComponent: () => import('@views/validador-firma-digital/validador-firma-digital.component').then(m => m.ValidadorFirmaDigitalComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Validador de Firma Digital',
          breadcrumb: 'Validador de Firma Digital'
        },
      },
      {
        path: 'configuracion',
        loadComponent: () => import('@views/configuracion/configuracion/configuracion.component').then(m => m.ConfiguracionComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Configuración',
          breadcrumb: 'Configuración'
        },
      },
      {
        path: 'parametrossistema',
        loadComponent: () => import('@views/configuracion-sistema/configuracion-sistema/configuracion-sistema.component').then(m => m.ConfiguracionSistemaComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Parámetros del Sistema',
          breadcrumb: 'Parámetros del Sistema'
        },
      },
      {
        path: 'personas',
        canActivate: [AuthGuardService],
        data: {
          title: 'Personas',
          breadcrumb: 'Personas'
        },
        children: [
          {
            path: '',
            loadComponent: () => import('@views/personas/listado-personas/listado-personas.component').then(m => m.ListadoPersonasComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Listado de Personas',
              breadcrumb: 'Listado de Personas'
            }
          },
          {
            path: 'importar',
            loadComponent: () => import('@views/importar-datos/importar-personas/importar-personas.component').then(m => m.ImportarPersonasComponent),
            canActivate: [AuthGuardService],
            canDeactivate: [CanDeactivateGuard],
            data: {
              title: 'Importar personas',
              breadcrumb: 'Importar personas'
            },
          },
          {
            // Migración Guid (2026-07-29): antes ':id' (numérico secuencial). Ver
            // docs/2026-07-29_Diseno_RBAC_Policies_ABAC.md y el registro de esta migración.
            path: ':guid',
            loadComponent: () => import('@views/personas/form-persona-view/form-persona-view/form-persona-view.component').then(m => m.FormPersonaViewComponent),
            canActivate: [AuthGuardService],
            canDeactivate: [CanDeactivateGuard],
            data: {
              title: 'Ver',
              breadcrumb: 'Ver'
            },
          },


        ]
      },


      {
        // La opción del menú sale de CasosUso.Path, que decía 'cotizacion' (singular) cuando
        // la ruta real siempre fue 'cotizaciones': el menú llevaba al 404. El seed ya está
        // corregido (ver Scripts/Migrations/2026-09-25_PathsMenu.sql); este redirect queda
        // para las bases que todavía no lo corrieron y para los links viejos.
        path: 'cotizacion',
        redirectTo: 'cotizaciones',
        pathMatch: 'full'
      },
      {
        path: 'calculadoraplazos',
        redirectTo: 'calculadora-plazos',
        pathMatch: 'full'
      },
      {
        path: 'cotizaciones',
        canActivate: [AuthGuardService],
        data: {
          title: 'Cotizaciones',
          breadcrumb: 'Cotizaciones'
        },
        children: [
          {
            path: '',
            loadComponent: () => import('@views/cotizaciones/list-cotizacion/list-cotizacion.component').then(m => m.ListCotizacionComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Listado de Cotizaciones',
              breadcrumb: 'Listado de Cotizaciones'
            }
          }
        ]
      },

      // ── Cotizaciones Fase 2 y 5: plantillas, modo rápido y tablero ──
      {
        path: 'plantillascotizacion',
        canActivate: [AuthGuardService],
        loadComponent: () => import('@views/cotizaciones/list-plantilla-cotizacion/list-plantilla-cotizacion.component').then(m => m.ListPlantillaCotizacionComponent),
        data: {
          title: 'Plantillas de Cotización',
          breadcrumb: 'Plantillas de Cotización'
        }
      },
      {
        // Alias por si el seed de CasosUso quedó en singular, como pasó con 'cotizacion'.
        path: 'plantillacotizacion',
        redirectTo: 'plantillascotizacion',
        pathMatch: 'full'
      },
      {
        path: 'cotizadorrapido',
        canActivate: [AuthGuardService],
        loadComponent: () => import('@views/cotizaciones/cotizador-rapido/cotizador-rapido.component').then(m => m.CotizadorRapidoComponent),
        data: {
          title: 'Cotizador Rápido',
          breadcrumb: 'Cotizador Rápido'
        }
      },
      {
        path: 'tablerocotizaciones',
        canActivate: [AuthGuardService],
        loadComponent: () => import('@views/cotizaciones/dashboard-cotizaciones/dashboard-cotizaciones.component').then(m => m.DashboardCotizacionesComponent),
        data: {
          title: 'Tablero de Cotizaciones',
          breadcrumb: 'Tablero de Cotizaciones'
        }
      },

      {
        path: 'terminoscondiciones',
        canActivate: [AuthGuardService],
        data: {
          title: 'Términos y Condiciones',
          breadcrumb: 'Términos y Condiciones'
        },
        children: [
          {
            path: '',
            loadComponent: () => import('@views/terminos-condiciones/list-termino-condicion/list-termino-condicion.component').then(m => m.ListTerminoCondicionComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Listado de Términos y Condiciones',
              breadcrumb: 'Listado de Términos y Condiciones'
            }
          }
        ]
      },

      {
        path: 'usuarios',
        data: {
              title: 'Usuarios',
              breadcrumb: 'Usuarios'
            },
        children: [
          {
            path: '',
            loadComponent: () => import('@views/usuarios/list-usuario/list-usuario.component').then(m => m.ListUsuarioComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Listado de Usuarios',
              breadcrumb: 'Listado de Usuarios'
            },
          },
        ]
      },
      {
        path: 'calendariojudicial',
        loadComponent: () => import('@views/calendario-judicial/list-calendario-judicial/list-calendario-judicial.component').then(m => m.ListCalendarioJudicialComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Calendario Judicial',
          breadcrumb: 'Calendario Judicial'
        }
      },
      {
        path: 'asuntos',
        loadComponent: () => import('@views/asuntos/asuntos/asuntos.component').then(m => m.AsuntosComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Listado de Asuntos',
          breadcrumb: 'Listado de Asuntos'
        }
      },
      {
        path: 'expedientes',
        children: [
          {
            path: '',
            loadComponent: () => import('@views/expedientes/list-expediente/list-expediente.component').then(m => m.ListExpedienteComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Listado de Expedientes',
              breadcrumb: 'Listado de Expedientes'
            },
          },
          {
            path: 'create',
            loadComponent: () => import('@views/expedientes/form-expediente-create/form-expediente-create.component').then(m => m.FormExpedienteCreateComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Nuevo',
              breadcrumb: 'Nuevo'
            },
          },
          {
            path: 'edit/:guid',
            loadComponent: () => import('@views/expedientes/form-expediente-update/form-expediente-update.component').then(m => m.FormExpedienteUpdateComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Modificar',
              breadcrumb: 'Modificar'
            },
          },
          {
            path: 'importar',
            loadComponent: () => import('@views/importar-datos/importar-expedientes/importar-expedientes.component').then(m => m.ImportarExpedientesComponent),
            canActivate: [AuthGuardService],
            canDeactivate: [CanDeactivateGuard],
            data: {
              title: 'Importar expedientes',
              breadcrumb: 'Importar expedientes'
            },
          },
          {
            path: ':guid',
            loadComponent: () => import('@views/expedientes/form-expediente-view/form-expediente-view.component').then(m => m.FormExpedienteViewComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Ver',
              breadcrumb: 'Ver'
            },
          },
        ]
      },


      // {
      //   path: 'empresa',
      //   component: EmpresaComponent,
      //   canActivate: [AuthGuardService  ],
      //   data: {
      //     title: '',
      //     breadcrumb: [
      //       {
      //         label: 'Empresa',
      //         url: ''
      //       }
      //     ]
      //   },
      // },
      {
        path: 'fileupload',
        loadComponent: () => import('@views/email/btn-send-email/btn-send-email.component').then(m => m.BtnSendEmailComponent),
        canActivate: [AuthGuardService],
        data: {
          breadcrumb: 'Enviar Correo'
        },
      },
      {
        path: 'documentos',
        loadComponent: () => import('@views/documentos/document-management/document-management.component').then(m => m.DocumentManagementComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Documentos',
          breadcrumb: [
            {
              label: 'Documentos',
              url: ''
            }
          ]
        },
      },
      {
        path: 'menues',
        loadComponent: () => import('@views/seguridad/menus/list-menu/list-menu.component').then(m => m.ListMenuComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Listado de Menus',
          breadcrumb: 'Listado de Menus'
        },
      },
      {
        // Auditoría QA (2026-08-10): restringido a SuperAdministrador también a nivel de
        // ruteo (ver SuperAdminGuardService) — la API ya lo restringía (Permisos.sql), pero
        // el guard evita que un usuario no-SuperAdmin llegue a ver la pantalla rota/vacía.
        path: 'permisos',
        loadComponent: () => import('@views/seguridad/permisos/list-permiso/list-permiso.component').then(m => m.ListPermisoComponent),
        canActivate: [AuthGuardService, SuperAdminGuardService],
        data: {
          title: 'Listado de Permisos',
          breadcrumb: 'Listado de Permisos'
        },
      },
      {
        path: 'perfiles',
        loadComponent: () => import('@views/seguridad/casos-uso/list-caso-uso/list-caso-uso.component').then(m => m.ListCasoUsoComponent),
        canActivate: [AuthGuardService, SuperAdminGuardService],
        data: {
          title: 'Listado de Perfiles',
          breadcrumb: 'Listado de Perfiles'
        },
      },
      {
        path: 'roles',
        loadComponent: () => import('@views/seguridad/roles/list-rol/list-rol.component').then(m => m.ListRolComponent),
        canActivate: [AuthGuardService, SuperAdminGuardService],
        data: {
          title: 'Listado de Roles',
          // Auditoría QA (2026-08-10): breadcrumb corregido, decía "Listado de Perfiles" (copy-paste
          // de la ruta 'perfiles' de arriba).
          breadcrumb: 'Listado de Roles'
        },
      },
      {
        path: 'whatsapp-sistema',
        loadComponent: () => import('@views/whatsapp/configuracion-whatsapp-sistema/configuracion-whatsapp-sistema.component').then(m => m.ConfiguracionWhatsAppSistemaComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'WhatsApp del Sistema',
          breadcrumb: 'WhatsApp del Sistema'
        },
      },
      // Auditoría QA (2026-08-09) — pantalla SuperAdmin de gestión de Facturame por Empresa
      // (ver EmpresaController.ListarParaGestionFacturame / SetFacturameHabilitado). El gateo real
      // de "solo SuperAdministrador" ocurre en la API; acá solo se exige estar autenticado, igual
      // que el resto de las pantallas de administración de esta sección.
      {
        path: 'gestion-facturame',
        loadComponent: () => import('@views/empresa/gestion-facturame/gestion-facturame.component').then(m => m.GestionFacturameComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Gestión de Facturación por Empresa',
          breadcrumb: 'Gestión de Facturación'
        },
      },
      {
        path: 'audiencias',
        children: [
          {
            path: '',
            loadComponent: () => import('@views/audiencias/list-audiencia/list-audiencia.component').then(m => m.ListAudienciaComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Listado de Audiencias',
              breadcrumb: 'Listado de Audiencias'
            },
          },
        ]
      },
      {
        path: 'casos',
        loadChildren: () =>
          import('@routes/caso.routes').then(m => m.CASO_ROUTES),
        canActivate: [AuthGuardService],
      },
      {
        path: 'registroscontable',
        children: [
          {
            path: '',
            loadComponent: () => import('@views/registros-contable/list-registro-contable/list-registro-contable.component').then(m => m.ListRegistroContableComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Listado de Registros Contable',
              breadcrumb: 'Listado de Registros Contable'
            },
          },

        ]
      },
      // Modulo Evidencias y Custodia (2026-10-06). Los `path` tienen que coincidir
      // exactamente con los CasosUso.Path sembrados en la migracion
      // 2026-10-06_ModuloEvidenciasCustodia.sql: el menu se arma desde ahi.
      {
        path: 'evidencias',
        children: [
          {
            path: '',
            loadComponent: () => import('@views/evidencias/list-evidencia/list-evidencia.component').then(m => m.ListEvidenciaComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Listado de Evidencias',
              breadcrumb: 'Listado de Evidencias'
            },
          },
        ]
      },
      {
        path: 'soportes',
        children: [
          {
            path: '',
            loadComponent: () => import('@views/evidencias/list-soporte/list-soporte.component').then(m => m.ListSoporteComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Listado de Soportes',
              breadcrumb: 'Listado de Soportes'
            },
          },
        ]
      },
      {
        path: 'custodia',
        children: [
          {
            path: '',
            loadComponent: () => import('@views/evidencias/list-movimiento-custodia/list-movimiento-custodia.component').then(m => m.ListMovimientoCustodiaComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Cadena de Custodia',
              breadcrumb: 'Cadena de Custodia'
            },
          },
        ]
      },
      {
        path: 'actividadesevidencia',
        children: [
          {
            path: '',
            loadComponent: () => import('@views/evidencias/list-actividad-evidencia/list-actividad-evidencia.component').then(m => m.ListActividadEvidenciaComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Actividad sobre Evidencias',
              breadcrumb: 'Actividad sobre Evidencias'
            },
          },
        ]
      },
      {
        path: 'ubicaciones',
        children: [
          {
            path: '',
            loadComponent: () => import('@views/evidencias/list-ubicacion/list-ubicacion.component').then(m => m.ListUbicacionComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Ubicaciones',
              breadcrumb: 'Ubicaciones'
            },
          },
        ]
      },
      {
        path: 'tiposprueba',
        children: [
          {
            path: '',
            loadComponent: () => import('@views/pruebas/tipoPrueba/list-tipo-prueba/list-tipo-prueba.component').then(m => m.ListTipoPruebaComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Tipos de Prueba',
              breadcrumb: 'Tipos de Prueba'
            },
          },
          {
            path: ':id',
            loadComponent: () => import('@views/pruebas/tipoPrueba/armador-plantilla-prueba/armador-plantilla-prueba.component').then(m => m.ArmadorPlantillaPruebaComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Armar plantilla de prueba',
              breadcrumb: 'Armar plantilla'
            },
          },
        ]
      },
      {
        path: 'formularios',
        children: [
          {
            path: '',
            loadComponent: () => import('@views/formularios/list-formulario/list-formulario.component').then(m => m.ListFormularioComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Formularios',
              breadcrumb: 'Formularios'
            },
          },
          {
            path: ':id/disenador',
            loadComponent: () => import('@views/formularios/disenador-formulario/disenador-formulario.component').then(m => m.DisenadorFormularioComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Diseñar formulario',
              breadcrumb: 'Diseñador'
            },
          },
          {
            path: ':id/registros',
            loadComponent: () => import('@views/formularios/registros/list-registro-formulario/list-registro-formulario.component').then(m => m.ListRegistroFormularioComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Registros del formulario',
              breadcrumb: 'Registros'
            },
          },
          {
            path: ':id/registros/nuevo',
            loadComponent: () => import('@views/formularios/registros/form-registro-formulario/form-registro-formulario.component').then(m => m.FormRegistroFormularioComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Nuevo registro',
              breadcrumb: 'Nuevo registro'
            },
          },
          {
            path: ':id/registros/:idRegistro/editar',
            loadComponent: () => import('@views/formularios/registros/form-registro-formulario/form-registro-formulario.component').then(m => m.FormRegistroFormularioComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Editar registro',
              breadcrumb: 'Editar registro'
            },
          },
          {
            path: ':id/registros/:idRegistro',
            loadComponent: () => import('@views/formularios/registros/view-registro-formulario/view-registro-formulario.component').then(m => m.ViewRegistroFormularioComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Registro',
              breadcrumb: 'Registro'
            },
          },
        ]
      },
      {
        path: 'juzgados',
        children: [
          {
            path: '',
            loadComponent: () => import('@views/juzgados/list-juzgado/list-juzgado.component').then(m => m.ListJuzgadosComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Listado de Juzgados',
              breadcrumb: 'Listado de Juzgados'
            },
          },
          {
            path: ':id',
            loadComponent: () => import('@views/juzgados/form-juzgado-view/form-juzgado-view.component').then(m => m.FormJuzgadoViewComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Ver',
              breadcrumb: 'Ver'
            },
          },


        ]
      },
      {
        path: 'fueros',
        children: [
          {
            path: '',
            loadComponent: () => import('@views/fuero/list-fuero/list-fuero.component').then(m => m.ListFueroComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Listado de Fueros',
              breadcrumb: 'Listado de Fueros'
            },
          },
        ]
      },
      {
        path: 'movimientoexpediente',
        children: [
          {
            path: '',
            loadComponent: () => import('@views/movimientos-expediente/list-movimiento-expediente/list-movimiento-expediente.component').then(m => m.ListMovimientoExpedienteComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Listado de movimientos de expediente',
              breadcrumb: 'Listado de movimientos de expediente'
            },
          },
        ]
      },
      {
        path: 'tareas',
        children: [
          {
            path: '',
            loadComponent: () => import('@views/tareas/list-tarea/list-tarea.component').then(m => m.ListTareaComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Listado de Tareas',
              breadcrumb: 'Listado de Tareas'
            },
          },
          {
            path: 'tablero',
            loadComponent: () => import('@views/tareas/list-tarea/tablero-kanban-tareas/tableroKanban.component').then(m => m.TableroKanbanComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Listado de Tareas',
              breadcrumb: 'Listado de Tareas'
            },
          },
          {
            path: ':id',
            loadComponent: () => import('@views/tareas/form-view-tarea/form-view-tarea.component').then(m => m.FormViewTareaComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Ver tarea',
              breadcrumb: 'Ver tarea'
            },
          },

        ]
      },
      {
        path: 'escritos',
        data: {
              title: 'Escritos',
              breadcrumb: 'Escritos'
            },
        children: [
          {
            path: '',
            loadComponent: () => import('@views/escritos/list-escrito/list-escrito.component').then(m => m.ListEscritoComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Listado de Escritos',
              breadcrumb: 'Listado de Escritos'
            },
          },
          {
            path: 'nuevo',
            loadComponent: () => import('@views/escrito-editor/escritos-editor/escritos-editor.component').then(m => m.EscritosEditorComponent),//DialogFormEscritoComponent,//CreateEscritoComponent,
            canActivate: [AuthGuardService],
            data: {
              title: 'Editar escritos',
              breadcrumb: 'Editar escritos'
            },
          },

        ]
      },

      // {
      //   path: 'plantillas',
      //   canActivate: [AuthGuardService],
      //   data: {
      //     title: 'Listado de Platillas',
      //     breadcrumb: 'Listado de Platillas'
      //   },
      //   children: [
      //     {
      //       path: '',
      //       component: ListPlantillaEscritoComponent,
      //       canActivate: [AuthGuardService],
      //       data: {
      //         title: 'Listado de Platillas',
      //         breadcrumb: 'Listado de Platillas'
      //       },
      //     },
      //     {
      //       path: 'create',
      //       component: PlantillaEscritoComponent,
      //       canActivate: [AuthGuardService],
      //       data: {
      //         title: 'Nueva Plantilla',
      //         breadcrumb: 'Nueva Plantilla'
      //       },
      //     },

      //   ]
      // },
      // {
      //   path: 'pruebastestimonial',
      //   component: ListadoPruebaTestimonialComponent,
      //   canActivate: [AuthGuardService],
      //   data: {
      //     title: 'Prueba Testimonial',
      //     breadcrumb: 'Prueba Testimonial'
      //   },
      // },

      // {
      //   path: 'pruebasinformativa',
      //   component: ListadoPruebaInformativaComponent,
      //   canActivate: [AuthGuardService],
      //   data: {
      //     title: 'Prueba Informativa',
      //     breadcrumb: 'Prueba Informativa'
      //   },
      // },

      {
        path: 'menusdesplegables',
        children: [
          {
            path: '',
            loadComponent: () => import('@views/menues-desplegables/menues-desplegables.component').then(m => m.MenuesDesplegablesComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Menu Desplegables',
              breadcrumb: 'Menu Desplegables'
            },
          },
          {
            path: ':id',
            loadComponent: () => import('./component/generic-table/generic-table.component').then(m => m.GenericTableComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Ver',
              breadcrumb: 'Ver'
            },
          },

        ]
      },
      {
        path: 'plazoprocesalregla',
        loadComponent: () => import('@views/plazo-procesal-regla/list-plazo-procesal-regla/list-plazo-procesal-regla.component').then(m => m.PlazoProcesalReglaListComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Reglas de plazos procesales',
          breadcrumb: 'Reglas de plazos procesales'
        }
      },
      {
        path: 'gestorias',
        loadComponent: () => import('@views/gestoria/list-gestoria/list-gestoria.component').then(m => m.ListGestoriaComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Listado de gestorias',
          breadcrumb: 'Listado de gestorias'
        }
      },
      {
        path: 'glosarios',
        loadComponent: () => import('@views/glosario/list-glosario/list-glosario.component').then(m => m.ListGlosarioComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Glosario',
          breadcrumb: 'Glosario'
        }
      },
      {
        path: 'conocimiento',
        loadComponent: () => import('@views/conocimiento/conocimiento.component').then(m => m.ConocimientoComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Base de Conocimiento',
          breadcrumb: 'Base de Conocimiento'
        }
      },
      {
        path: 'conocimientocategorias',
        loadComponent: () => import('@views/conocimiento/categorias/list-categoria/list-categoria.component').then(m => m.ListCategoriaComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Categorías de Conocimiento',
          breadcrumb: 'Categorías de Conocimiento'
        }
      },
      {
        path: 'conocimientotiposarticulo',
        loadComponent: () => import('@views/conocimiento/tipos-articulo/list-tipo-articulo/list-tipo-articulo.component').then(m => m.ListTipoArticuloComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Tipos de Artículo de Conocimiento',
          breadcrumb: 'Tipos de Artículo de Conocimiento'
        }
      },
      {
        path: 'conocimientoetiquetas',
        loadComponent: () => import('@views/conocimiento/etiquetas/list-etiqueta/list-etiqueta.component').then(m => m.ListEtiquetaComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Etiquetas de Conocimiento',
          breadcrumb: 'Etiquetas de Conocimiento'
        }
      },
      {
        // El ABM de categorías vive dentro de esta misma pantalla (árbol a la izquierda),
        // por eso 'catalogocategorias' apunta al mismo componente.
        path: 'catalogoitems',
        loadComponent: () => import('@views/catalogo/catalogo.component').then(m => m.CatalogoComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Catálogo de Servicios',
          breadcrumb: 'Catálogo de Servicios'
        }
      },
      {
        path: 'catalogocategorias',
        loadComponent: () => import('@views/catalogo/catalogo.component').then(m => m.CatalogoComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Categorías del Catálogo',
          breadcrumb: 'Categorías del Catálogo'
        }
      },
      {
        path: 'herramientas',
        loadComponent: () => import('@views/herramientas/herramienta/herramienta.component').then(m => m.HerramientasComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Herramientas',
          breadcrumb: 'Herramientas'
        },
        children: [
          {
            path: 'calculadora-honorarios',
            loadComponent: () => import('@views/calculadora-honorarios/calculadora-honorarios.component').then(m => m.CalculadoraHonorariosComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Calculadora de Honorarios',
              breadcrumb: 'Calculadora de Honorarios'
            }
          },
          {
            path: 'calculadora-plazos',
            loadComponent: () => import('@views/calculadora-plazos/calculadora-plazo/calculadora-plazos.component').then(m => m.CalculadoraPlazosComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Calculadora de Plazos',
              breadcrumb: 'Calculadora de Plazos'
            }
          },

          {
            path: 'actualizacion-importes',
            loadComponent: () => import('@views/calculo-actualizacion/calculo-actualizacion.component').then(m => m.CalculoActualizacionComponent),
            canActivate: [AuthGuardService],
            data: {
              title: 'Actualizacion de importes',
              breadcrumb: 'Actualizacion de importes'
            }
          },
        ]
      },

      {
        path: 'feriados',
        loadComponent: () => import('@views/feriado/list-feriado/list-feriado.component').then(m => m.ListFeriadoComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Feriados',
          breadcrumb: 'Feriados'
        }
      },
      {
        path: 'tipocalculoplazo',
        loadComponent: () => import('@views/tipo-calculo-plazo/list-tipo-calculo-plazo/list-tipo-calculo-plazo.component').then(m => m.ListTipoCalculoPlazoComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Indices de actualización de expediente',
          breadcrumb: 'Tipos de calculo para plazos'
        }
      },
      {
        path: 'eventosagenda',
        loadComponent: () => import('@component/general-calendar/general-calendar-list/general-calendar.component').then(m => m.GeneralCalendarComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Listado de eventos',
          breadcrumb: 'Eventos'
        }
      },
      {
        // Tiene que coincidir EXACTO con GoogleAuth:CalendarRedirectUri en appsettings del
        // backend (Google rechaza el canje de code si no matchean carácter por carácter).
        path: 'calendario/google-callback',
        loadComponent: () => import('@component/google-calendar-callback/google-calendar-callback.component').then(m => m.GoogleCalendarCallbackComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Vinculación con Google Calendar',
          breadcrumb: 'Google Calendar'
        }
      },
      {
        // Tiene que coincidir EXACTO con MicrosoftAuth:CalendarRedirectUri en appsettings
        // del backend (Microsoft rechaza el canje de code si no matchean carácter por
        // carácter). Mismo rol que la ruta de Google de arriba.
        path: 'calendario/outlook-callback',
        loadComponent: () => import('@component/outlook-calendar-callback/outlook-calendar-callback.component').then(m => m.OutlookCalendarCallbackComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Vinculación con Outlook Calendar',
          breadcrumb: 'Outlook Calendar'
        }
      },
      {
        path: 'jurisdicciones',
        loadComponent: () => import('@views/jurisdiccion/list-jurisdiccion/list-jurisdiccion.component').then(m => m.ListJurisdiccionComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Listado de jurisdicciones',
          breadcrumb: 'Jurisdicciones'
        }
      },
      {
        path: 'tiposindiceactualizacion',
        loadComponent: () => import('@views/tipo-indice-actualizacion/list-tipo-indice-actualizacion.component/list-tipo-indice-actualizacion.component').then(m => m.ListTipoIndiceActualizacionComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Listado de tipos de indices',
          breadcrumb: 'Tipos de indices'
        }
      },
      {
        path: 'tipossexo',
        loadComponent: () => import('@views/tipo-sexo/list-tipo-sexo/list-tipo-sexo.component').then(m => m.ListTipoSexoComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Listado de tipos de sexo',
          breadcrumb: 'Tipos de Sexo'
        }
      },
      {
        path: 'indicesactualizacionexpediente',
        loadComponent: () => import('@views/indice-actualizacion-expediente/list-indice-actualizacion-expediente.component/list-indice-actualizacion-expediente.component').then(m => m.ListIndiceActualizacionExpedienteComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Listado de indices',
          breadcrumb: 'Indices'
        }
      },
      {
        path: 'subespecialidad',
        loadComponent: () => import('@views/sub-especialidad/list-sub-especialidad/list-sub-especialidad.component').then(m => m.ListSubEspecialidadComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Listado de sub-especialidades',
          breadcrumb: 'Sub-especialidades'
        }
      },
      {
        path: 'escalahonorarios',
        loadComponent: () => import('@views/escala-honorario/list-escala-honorario/list-escala-honorario.component').then(m => m.ListEscalaHonorarioComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Listado de escalas de honorarios',
          breadcrumb: 'Escala de honorarios'
        }
      },
      {
        path: 'domiciliosconstituidos',
        loadComponent: () => import('@views/domicilio-constituido/list-domicilio-constituido/list-domicilio-constituido.component').then(m => m.ListDomicilioConstituidoComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Listado de domicilios constituidos',
          breadcrumb: 'Domicilios constituidos'
        }
      },
      {
        path: 'notificaciones',
        loadComponent: () => import('@views/notificaciones/list-notificacion/list-notificacion.component').then(m => m.ListNotificacionComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Listado de notificaciones',
          breadcrumb: 'Notificaciones'
        }
      },

      {
        path: 'cotizacionesmonedas',
        loadComponent: () => import('@views/cotizacion-moneda/list-cotizacion-moneda/list-cotizacion-moneda.component').then(m => m.ListCotizacionMonedaComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Listado de cotizaciones de monedas',
          breadcrumb: 'Cotizaciones de Monedas'
        }
      },

      {
        path: 'auditorialog',
        loadComponent: () => import('@views/auditoria-log/list-auditoria-log/list-auditoria-log.component').then(m => m.ListAuditoriaLogComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Listado de auditoria',
          breadcrumb: 'Log de auditoria'
        }
      },

        {
        path: 'firma',
        loadComponent: () => import('@views/firma-masiva/firma-masiva.component').then(m => m.FirmaMasivaComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Herramienta de Firma',
          breadcrumb: 'Herramienta de Firma'
        }
      },
      {
        path: 'actuacionexpediente',
        loadComponent: () => import('@views/actuacion-expediente/list-actuacion-expediente/list-actuacion-expediente.component').then(m => m.ListActuacionExpedienteComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Actuaciones de Expedientes',
          breadcrumb: 'Actuaciones de Expedientes'
        }
      },
      {
        path: 'criteriospuntuacion',
        loadComponent: () => import('@views/criterio-puntuacion/criterio-puntuacion-empresa/criterio-puntuacion-empresa.component').then(m => m.CriterioPuntuacionEmpresaComponent),
        canActivate: [AuthGuardService],
        data: {
          title: 'Criterios de Puntuación',
          breadcrumb: 'Criterios de Puntuación'
        }
      },


      {
        // Auditoría UI (2026-07-30): antes redirigía en silencio a /dashboard;
        // ahora muestra una página 404 real (usuario autenticado, dentro del layout).
        path: "**", loadComponent: () => import('@views/errors/error404/error404.component').then(m => m.Error404Component)
      }
    ]
  },


  {
    // Auditoría UI (2026-07-30): idem, para rutas desconocidas sin sesión iniciada.
    path: "**", loadComponent: () => import('@views/errors/error404/error404.component').then(m => m.Error404Component)
  }
]

@NgModule({
  declarations: [],
  imports: [
    CommonModule,
    RouterModule.forRoot(routes, { useHash: true })
  ],
  exports: [RouterModule]

})
export class AppRoutingModule { }
